/* eslint-disable indent */
import { followArtist, unfollowArtist } from 'api/artists'
import { updateUserProfilePreferences } from 'api/profiles'
import classNames from 'classnames'
import { ArtistDescription } from 'components/artists/ArtistDescription'
import { Badge } from 'components/common/Badge'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Separator } from 'components/common/Separator'
import { StaticIcon } from 'components/common/StaticIcon'
import { Switch } from 'components/common/Switch'
import routes from 'constants/routes'
import { useAuth, useAuthModal, useOverlay } from 'contexts'
import { useUserProfile } from 'hooks'
import { useFollowedArtist } from 'hooks/artists/useFollowedArtist'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import { Fragment, useEffect, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useMutation, useQueryClient } from 'react-query'
import { FollowArtistPreferences } from 'types'
import { Artist } from 'utils/data'
import { getArtistImageUrl } from 'utils/image'

const SocialLinks = ({
  socialLinks,
  withSeparator,
  className,
  listClassName,
  listItemClassName,
  linkClassName,
  iconClassName,
  iconSize,
}: {
  socialLinks?: Artist['socialLinks']
  withSeparator?: boolean
  className?: string
  listClassName?: string
  listItemClassName?: string
  linkClassName?: string
  iconClassName?: string
  iconSize?: number
}) => {
  if (!socialLinks) return null

  return (
    <div className={`flex items-center ${className}`}>
      {withSeparator &&
        (socialLinks.instagram || socialLinks.website || socialLinks.email) && (
          <span className='h-[1em] w-px bg-cool-400'></span>
        )}
      <ul className={`flex items-center space-x-2 ${listClassName}`}>
        {socialLinks.instagram && (
          <li className={listItemClassName}>
            <a
              href={socialLinks.instagram}
              className={linkClassName}
              target='_blank'
              rel='noreferrer noopener'
              title='Instagram'
              aria-label='Instagram'
            >
              <span className='hidden'>Instagram</span>
              <Icon
                name='instagram'
                size={iconSize}
                className={iconClassName}
              />
            </a>
          </li>
        )}
        {socialLinks.website && (
          <li className={listItemClassName}>
            <a
              href={socialLinks.website}
              className={linkClassName}
              target='_blank'
              rel='noreferrer noopener'
              title='Website'
              aria-label='Website'
            >
              <span className='hidden'>Website</span>
              <Icon
                name='world-net'
                size={iconSize}
                className={iconClassName}
              />
            </a>
          </li>
        )}
        {socialLinks.email && (
          <li className={listItemClassName}>
            <a
              href={
                socialLinks.email.includes('@')
                  ? `mailto:${socialLinks.email}`
                  : socialLinks.email
              }
              className={linkClassName}
              target='_blank'
              rel='noreferrer noopener'
              title='Email'
              aria-label='Email'
            >
              <span className='hidden'>Email</span>
              <Icon name='mail' size={iconSize} className={iconClassName} />
            </a>
          </li>
        )}
      </ul>
    </div>
  )
}

const notifications: {
  label: string
  sublabel: string
  key?: keyof FollowArtistPreferences
}[] = [
  {
    label: 'email_preferences',
    sublabel: 'you_can_change_these_at_any_time',
  },
  {
    label: 'new_lowest_listing',
    sublabel: 'sent_when_there_is_a_new_lowest_listing',
    key: 'newLowestAskEmail',
  },
  {
    label: 'new_highest_offer',
    sublabel: 'sent_when_there_is_a_new_highest_offer',
    key: 'newHighestBidEmail',
  },
  {
    label: 'new_sale',
    sublabel: 'sent_when_an_artwork_by_this_artist_is_sold',
    key: 'newSaleEmail',
  },
  {
    label: 'new_release',
    sublabel: 'sent_when_artist_release',
    key: 'newReleaseEmail',
  },
]

const FollowArtistModal = ({
  name,
  followArtistId,
}: {
  name: string
  followArtistId?: number
}) => {
  const { t } = useTranslation('single_artist')
  const { setIsOpen } = useOverlay()
  const { data: userProfile } = useUserProfile()

  const [preferences, setPreferences] = useState<FollowArtistPreferences>({
    newLowestAskEmail: Boolean(userProfile?.followArtistNewLowestAsk),
    newHighestBidEmail: Boolean(userProfile?.followArtistNewHighestBid),
    newSaleEmail: Boolean(userProfile?.followArtistNewSale),
    newReleaseEmail: Boolean(userProfile?.followArtistNewRelease),
  })

  const onClick = async (key: keyof FollowArtistPreferences) => {
    const newPreferences: FollowArtistPreferences = {
      ...preferences,
      [key]: !preferences[key],
    }
    setPreferences(newPreferences)

    if (followArtistId) {
      try {
        const payload = {
          followArtistNewLowestAsk: newPreferences.newLowestAskEmail,
          followArtistNewHighestBid: newPreferences.newHighestBidEmail,
          followArtistNewSale: newPreferences.newSaleEmail,
          followArtistNewRelease: newPreferences.newReleaseEmail,
        }
        const { profile, error } = await updateUserProfilePreferences(payload)

        if (error || !profile) throw new Error(error?.message)
      } catch (err) {
        console.error(err)
      }
    }
  }

  return (
    <div className='flex w-full max-w-[600px] flex-col rounded-smd bg-cool-50'>
      <header className='flex items-center justify-between border-b border-cool-300 p-6'>
        <div className='flex items-center space-x-2'>
          <Icon
            name='filled-success'
            filled
            className='text-success-default'
            size={24}
          />
          <h5 className='font-body text-base font-medium tracking-normal md:text-xl'>
            {t('you_are_following', { artistName: name })}
          </h5>
        </div>

        <button type='button' onClick={() => setIsOpen(false)}>
          <Icon name='close' className='text-cool-600' size={18} />
        </button>
      </header>

      <main className='p-6'>
        <div className='flex flex-col space-y-6 text-sm text-cool-600'>
          <p>{t('follow_your_favourite_artists')}</p>
          <p>
            <Trans
              i18nKey='single_artist:see_all_the_artists'
              components={{
                link: (
                  <Link
                    href={routes.following.artists}
                    className='text-secondary-default'
                    onClick={() => setIsOpen(false)}
                  />
                ),
              }}
            />
          </p>
        </div>

        <div className='mt-5 flex w-full flex-col rounded-sm border border-warm-100 bg-warm-50 px-5 py-4'>
          {notifications.map(({ label, sublabel, key }, index) => (
            <Fragment key={label}>
              <div className='flex items-start justify-between'>
                <div>
                  <h6 className='font-body text-sm font-medium tracking-normal'>
                    {t(label)}
                  </h6>
                  <p className='text-sm text-cool-600'>{t(sublabel)}</p>
                </div>

                {key && (
                  <Switch
                    checked={preferences[key]}
                    onClick={() => onClick(key)}
                    className={classNames(
                      preferences[key] ? 'bg-primary-default' : ''
                    )}
                  />
                )}
              </div>

              {notifications[index + 1] && (
                <Separator className='my-3' color='cool-300' />
              )}
            </Fragment>
          ))}
        </div>
      </main>
    </div>
  )
}

type Props = {
  className?: string
  nationalities: {
    name: string
    countryCode: string
  }[]
} & Artist

export const ArtistSummary = ({
  id,
  nationalities = [],
  name,
  description,
  description2,
  className,
  birthYear,
  socialLinks,
  avatar,
}: Props) => {
  const { t } = useTranslation('single_artist')
  const { data: userProfile } = useUserProfile()
  const { user, isAuthenticated } = useAuth()
  const { setIsOpen, setConfig } = useOverlay()
  const { setState: setAuthModalState, setIsOpen: setAuthModalIsOpen } =
    useAuthModal()
  const queryClient = useQueryClient()

  const followedArtistQueryKey = ['followed-artist', id]

  const {
    data: followArtistId,
    refetch: refetchFollowArtist,
    isFetching: isFetchingFollowedArtist,
  } = useFollowedArtist({ id })

  const { mutate: mutateArtistFollow, isLoading: isLoadingFollowMutation } =
    useMutation({
      mutationKey: ['follow', userProfile?.id, id],
      mutationFn: async (currentFollowedArtistId: number | undefined) => {
        if (isLoadingFollowMutation || isFetchingFollowedArtist) return

        if (currentFollowedArtistId) {
          return unfollowArtist(currentFollowedArtistId)
        }
        return followArtist({
          artistId: id,
          newHighestBidEmail: Boolean(userProfile?.followArtistNewHighestBid),
          newLowestAskEmail: Boolean(userProfile?.followArtistNewLowestAsk),
          newSaleEmail: Boolean(userProfile?.followArtistNewSale),
          newReleaseEmail: Boolean(userProfile?.followArtistNewRelease),
        })
      },
      async onMutate(currentFollowedArtistId) {
        if (isLoadingFollowMutation || isFetchingFollowedArtist) return

        await queryClient.cancelQueries(followedArtistQueryKey)

        // if currentSavedArtworkId is provided, user is unfollowing, so we update the ui to undefined
        // otherwise, the user is following the artwork and we set the id to 1 (just a placeholder) because we don't know beforehand what id the following will have
        // the id will be updated when the query is refetched
        queryClient.setQueryData(
          followedArtistQueryKey,
          currentFollowedArtistId ? null : 1
        )

        return { previousFollowedArtistId: currentFollowedArtistId }
      },
      onSuccess(data) {
        if (data?.error) {
          throw new Error(data.error.message)
        }

        if (data?.id) {
          setConfig({
            className: 'flex items-center px-5',
            body: <FollowArtistModal name={name} followArtistId={data.id} />,
          })
          setIsOpen(true)
        }

        refetchFollowArtist()
      },
      onError(error, _, context) {
        // in case of error, revert the query data
        if (context?.previousFollowedArtistId !== undefined) {
          queryClient.setQueryData(
            followedArtistQueryKey,
            context.previousFollowedArtistId
          )
        }

        console.error(error)
      },
    })

  useEffect(() => {
    return () => {
      setIsOpen(false)
    }
  }, [])

  const imgAlt = t('single_artist:image_alt', {
    artistName: name,
    nationality: nationalities?.map(n => n.name).join(', ') || '',
    dateOfBirth: birthYear || '',
  })

  const onFollowBtnClick = () => {
    if (!user) {
      setAuthModalState('register')
      setAuthModalIsOpen(true)
      return
    }

    mutateArtistFollow(followArtistId)
  }

  const followBtn = (
    <Button
      style={followArtistId ? 'outline' : 'solid-black'}
      pill
      size='sm'
      onClick={onFollowBtnClick}
    >
      <StaticIcon name={followArtistId ? 'check' : 'plus'} />

      <span className='ml-2.5'>
        {followArtistId ? t('common:following') : t('common:follow')}
      </span>
    </Button>
  )

  return (
    <div className={`flex flex-col  ${className}`}>
      <div className='my-5 flex md:m-0'>
        <div className='relative mr-8 hidden h-auto max-h-[200px] min-h-[200px] w-full min-w-[200px] max-w-[200px] md:flex xl:max-h-[225px] xl:min-h-[225px] xl:min-w-[225px] xl:max-w-[225px]'>
          <Image
            src={getArtistImageUrl(name, avatar, 'large')}
            alt={imgAlt}
            fill
            className='rounded-smd object-cover'
            quality={40}
            sizes='(max-width:768px) 0vw, 20vw'
            fetchPriority='high'
            loading='eager'
          />
        </div>

        <div className='flex flex-col'>
          <div className='flex'>
            <div className='relative mr-3 h-[125px] w-[125px] rounded-full sm:h-[140px] sm:w-[140px] md:hidden'>
              <Image
                src={getArtistImageUrl(name, avatar, 'thumbnail')}
                alt={imgAlt}
                fill
                className='rounded-full object-cover'
                quality={35}
                sizes='(max-width:639px) 30vw,
                   (max-width:767px) 20vw,
                   0vw'
                fetchPriority='high'
                loading='eager'
              />

              <SocialLinks
                className='absolute bottom-3 left-1/2 z-[1] -translate-x-1/2'
                socialLinks={socialLinks}
                iconClassName='bg-black bg-opacity-40 flex p-2 rounded-full text-cool-50'
                iconSize={16}
              />
            </div>

            <div className='flex flex-col'>
              <div className='flex items-center'>
                <h1 className='text-xl font-medium tracking-normal sm:text-2xl md:text-3xl'>
                  {name}
                </h1>

                <div className='ml-3 hidden md:block'>{followBtn}</div>
              </div>

              <div className='flex flex-wrap items-center'>
                {nationalities.map(nationality => (
                  <div
                    key={nationality.name}
                    className='mr-2 flex items-center space-x-2'
                  >
                    <ReactCountryFlag
                      countryCode={nationality.countryCode}
                      svg
                      alt={nationality.name}
                    />
                    <span className='text-base text-cool-600'>
                      {nationality.name}
                    </span>
                  </div>
                ))}
                {birthYear && (
                  <span className='-ml-2 text-base text-cool-600'>
                    , b. {birthYear}
                  </span>
                )}

                <div className='hidden items-center md:flex'>
                  <span className='ml-3 mr-4 text-cool-400'>|</span>

                  <SocialLinks
                    socialLinks={socialLinks}
                    iconClassName='text-cool-600'
                    iconSize={16}
                  />
                </div>
              </div>

              <div className='mt-3 md:hidden'>{followBtn}</div>
            </div>

            <div className='ml-auto hidden h-16 w-16 md:block'>
              <Badge type='Artist' />
            </div>
          </div>

          <ArtistDescription description={description2} />
        </div>
      </div>
    </div>
  )
}
